/**
 * The week strip under the greeting: Monday to Sunday, today marked, the
 * selected day filled, a dot under every day that has work. The arrows beside
 * "Ma" (or a sideways swipe on the days) move a week at a time, "Ma" comes
 * back to today and the calendar opens the month view for anything further out.
 *
 * Days are Budapest calendar dates ('YYYY-MM-DD'); `tripCounts` maps a day to
 * how many trips it has, from the week's one fetch.
 */
import React, { useRef } from 'react';
import {
  addDays, dayAriaLabel, dayOfMonth, monthLabel, weekdayShort, weekOf,
} from '../portalFormat';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from './Icons';

// A deliberate sideways swipe, not a wobbly vertical scroll.
const SWIPE_PX = 50;

export default function WeekStrip({
  selected, today, tripCounts, onSelect, onOpenCalendar,
}) {
  const days = weekOf(selected);
  const touchStart = useRef(null);

  const moveWeek = (weeks) => onSelect(addDays(selected, weeks * 7));

  const onTouchStart = (event) => {
    const touch = event.touches[0];
    touchStart.current = { x: touch.clientX, y: touch.clientY };
  };

  const onTouchEnd = (event) => {
    const start = touchStart.current;
    touchStart.current = null;
    if (!start) return;
    const touch = event.changedTouches[0];
    const dx = touch.clientX - start.x;
    if (Math.abs(dx) < SWIPE_PX || Math.abs(dx) < Math.abs(touch.clientY - start.y)) return;
    moveWeek(dx < 0 ? 1 : -1);
  };

  return (
    <nav className="pp-week" aria-label="Napok">
      <div className="pp-week__bar">
        <span className="pp-week__month">{monthLabel(selected)}</span>
        <span className="pp-week__tools">
          <button
            type="button"
            className="pp-week__icon"
            aria-label="Előző hét"
            onClick={() => moveWeek(-1)}
          >
            <ChevronLeftIcon size={20} />
          </button>
          <button
            type="button"
            className="pp-btn pp-btn--outline pp-week__today"
            onClick={() => onSelect(today)}
            aria-pressed={selected === today}
          >
            Ma
          </button>
          <button
            type="button"
            className="pp-week__icon"
            aria-label="Következő hét"
            onClick={() => moveWeek(1)}
          >
            <ChevronRightIcon size={20} />
          </button>
          <button
            type="button"
            className="pp-week__icon"
            aria-label="Naptár"
            onClick={onOpenCalendar}
          >
            <CalendarIcon size={20} />
          </button>
        </span>
      </div>

      {/* The days get the whole width: 44px+ targets from a 360px phone up. */}
      <div className="pp-week__row" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <ul className="pp-week__days">
          {days.map((day) => {
            const trips = tripCounts[day] || 0;
            const classes = ['pp-week__day'];
            if (day === today) classes.push('pp-week__day--today');
            if (day === selected) classes.push('pp-week__day--selected');
            return (
              <li key={day}>
                <button
                  type="button"
                  className={classes.join(' ')}
                  aria-label={dayAriaLabel(day, trips)}
                  aria-pressed={day === selected}
                  aria-current={day === today ? 'date' : undefined}
                  onClick={() => onSelect(day)}
                >
                  <span className="pp-week__name" aria-hidden="true">{weekdayShort(day)}</span>
                  <span className="pp-week__date" aria-hidden="true">{dayOfMonth(day)}</span>
                  <span
                    className={`pp-week__dot${trips ? ' pp-week__dot--on' : ''}`}
                    aria-hidden="true"
                  />
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
